import React, { useCallback } from 'react';
import { Box, Typography, FormControl, Select, MenuItem } from '@mui/material';
import { FormInput } from '../../Common';
import { useFormContext } from 'react-hook-form';
import type { OccasionLocale } from '../../../types/category.types';

export const SUPPORTED_LOCALES: Record<string, string> = {
  en: 'English',
  ar: 'Arabic',
};

const LOCALE_KEYS = Object.keys(SUPPORTED_LOCALES);

export interface ExperienceFormContentProps {
  loading: boolean;
  previewImage: string | null;
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  locale: OccasionLocale;
  setLocale: React.Dispatch<React.SetStateAction<OccasionLocale>>;
  currentLang: string;
  setCurrentLang: React.Dispatch<React.SetStateAction<string>>;
  inputId?: string;
  contentClassName?: string;
}

const ExperienceFormContent: React.FC<ExperienceFormContentProps> = ({
  loading,
  previewImage,
  onImageUpload,
  locale,
  setLocale,
  currentLang,
  setCurrentLang,
  inputId = 'experience-image-upload',
  contentClassName = 'add-experience-form-content',
}) => {
  const { setValue, getValues } = useFormContext<{ title: string; description: string }>();

  const handleLanguageChange = useCallback(
    (newLang: string) => {
      const values = getValues();
      const nextLocale: OccasionLocale = {
        ...locale,
        [currentLang]: {
          title: values.title?.trim() ?? '',
          description: values.description?.trim() ?? '',
        },
      };
      setLocale(nextLocale);
      setCurrentLang(newLang);
      const entry = nextLocale[newLang];
      setValue('title', entry?.title ?? '', { shouldDirty: true });
      setValue('description', entry?.description ?? '', { shouldDirty: true });
    },
    [currentLang, locale, setLocale, setCurrentLang, getValues, setValue]
  );

  return (
    <Box className={contentClassName}>
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <Select
          value={currentLang}
          onChange={(e) => handleLanguageChange(e.target.value)}
          displayEmpty
          disabled={loading}
          sx={{
            backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            borderRadius: 'var(--palette-light-4-value)',
            fontSize: 'var(--palette-light-font-size-default-value)',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--palette-light-border-primary-value-hex)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--palette-light-brand-primary-value-hex)',
              borderWidth: '1px',
            },
          }}
        >
          {LOCALE_KEYS.map((code) => (
            <MenuItem key={code} value={code}>
              {SUPPORTED_LOCALES[code]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box
        className="experience-image-upload"
        onClick={() => document.getElementById(inputId)?.click()}
      >
        {previewImage ? (
          <Box
            component="img"
            src={previewImage}
            alt="Preview"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <>
            <Box sx={{ width: '96px', height: '96px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M42.41 42.41C38.91 45.91 34.09 48 28.5 48H19.5C13.91 48 9.09 45.91 5.59 42.41C2.09 38.91 0 34.09 0 28.5V19.5C0 13.91 2.09 9.09 5.59 5.59C9.09 2.09 13.91 0 19.5 0H28.5C34.09 0 38.91 2.09 42.41 5.59C45.91 9.09 48 13.91 48 19.5V28.5C48 34.09 45.91 38.91 42.41 42.41Z" stroke="#181818" strokeWidth="2"/>
                <path d="M20.4 27.76L14.02 13.88L7.5 24.16" stroke="#181818" strokeWidth="1.2"/>
                <circle cx="18.5" cy="18.5" r="3.5" stroke="#181818" strokeWidth="1.2"/>
              </svg>
            </Box>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)', textAlign: 'center' }}>
              Click or drag image file<br />to this area to upload
            </Typography>
          </>
        )}
        <input
          id={inputId}
          type="file"
          accept="image/*"
          onChange={onImageUpload}
          style={{ display: 'none' }}
        />
      </Box>

      <FormInput
        name="title"
        label="Experience title"
        placeholder="Enter experience title"
        disabled={loading}
        required
      />
      <FormInput
        name="description"
        label="Description"
        placeholder="Enter experience description"
        disabled={loading}
        required
        multiline
        rows={4}
      />
    </Box>
  );
};

export default ExperienceFormContent;
